import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SaveGreyIcon from 'shared/assets/src-assets/png/gem_web.png';
import {
	UnknownAnimalTableItem,
	filteredStemAnimalsByUnknownAnimals,
} from 'shared/state/ducks/stem-animals/operations';
import { getUnknownAnimals, mapUnknownAnimals } from 'shared/state/ducks/unknown-animals/operation';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { FlexRow } from 'web/view/components/basic-flex/flex-row';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldModal } from 'web/view/components/skiold-components/skiold-modal/skiold-modal';
import SkioldTableGrid from 'web/view/components/skiold-components/skiold-table/skiold-table-grid/skiold-table-grid';
import { GroupedHeader } from 'web/view/components/skiold-components/skiold-table/skiold-table-grid/skiold-table-grid-grouped-header';
import { SkioldTouchableOpacity } from 'web/view/components/skiold-components/skiold-touchable-opacity';
import { SkioldIconSpinner } from 'web/view/components/utils/skiold-icon-spinner';
import SowEvents from '../sow-events/sow-events';
import './map-unknown-animals.scss';
export const MapUnknownAnimals: FC = React.memo(() => {
	//init component
	const dispatch = useDispatch();
	const [entranceModalOpen, setEntranceModalOpen] = React.useState(false);
	const [selectedItems, setSelectedItems] = React.useState<UnknownAnimalTableItem[]>([]);
	const siteId = useSelector((state: WebAppState) => state.profile.active?.siteId);
	const stemAnimals = useSelector(filteredStemAnimalsByUnknownAnimals);
	const showSpinner = useSelector((state: WebAppState) => state.unknownAnimals.pending);
	const [unknownAnimalItem, setUnknownAnimalItem] = React.useState<UnknownAnimalTableItem>();

	const pens = useSelector((state: WebAppState) => state.locations.pens);

	const selectedSectionId = useMemo(() => {
		const selectedPen = pens.find(p => p.id === unknownAnimalItem?.newPenId);
		return selectedPen?.sectionId;
	}, [pens, unknownAnimalItem?.newPenId]);

	//init useEffect
	useEffect(() => {
		if (siteId) {
			dispatch(getUnknownAnimals(siteId));
		}
	}, [dispatch, siteId]);

	const openModal = useCallback(
		(unknownAnimalItem?: UnknownAnimalTableItem) => () => {
			if (unknownAnimalItem) {
				setUnknownAnimalItem(unknownAnimalItem);
			}
			setEntranceModalOpen(true);
		},
		[],
	);

	const onUnknownAnimalCreates = useCallback(() => {
		if (!unknownAnimalItem) {
			return;
		}

		dispatch(mapUnknownAnimals([unknownAnimalItem]));
		setUnknownAnimalItem(undefined); // Reset selected item after creating
	}, [dispatch, unknownAnimalItem]);

	const closeModal = useCallback(() => {
		setEntranceModalOpen(false);
	}, []);

	const skioldAnimalNumberCell = useCallback(
		(animal: UnknownAnimalTableItem) => {
			if (animal.stemAnimalId) {
				return animal.animalNumber;
			}

			return (
				<SkioldTouchableOpacity className="positionRelative" onPress={openModal(animal)}>
					<div className="create-animal-text">{localized('CreateAnimal')}</div>
				</SkioldTouchableOpacity>
			);
		},
		[openModal],
	);

	// setup table
	const columns = useMemo(() => {
		return [
			{
				name: 'animalNumber',
				title: localized('skiold'),
				getCellValue: skioldAnimalNumberCell,
			},
			{
				name: 'unknownAnimalNumber',
				title: localized('elistar'),
			},
			{
				name: 'transponder',
				title: localized('transponder'),
			},
			{
				name: 'skioldLocation',
				title: localized('skiold'),
			},
			{
				name: 'elistarLocation',
				title: localized('elistar'),
			},
		];
	}, []);
	const columnExtensions = useMemo(() => {
		return [
			{
				columnName: 'animalNumber',
				width: 150,
			},
			{
				columnName: 'unknownAnimalNumber',
				width: 150,
			},
			{
				columnName: 'transponder',
				width: 150,
			},
			{
				columnName: 'skioldLocation',
				width: 150,
			},
			{
				columnName: 'elistarLocation',
				width: 150,
			},
		];
	}, []);
	const groupedColumns = useMemo(() => {
		return [
			{
				title: localized('animalNumber'),
				children: [{ columnName: 'animalNumber' }, { columnName: 'unknownAnimalNumber' }],
			},
			{
				title: localized('location'),
				children: [{ columnName: 'skioldLocation' }, { columnName: 'elistarLocation' }],
			},
		] as GroupedHeader[];
	}, []);

	const mapUnknownAnimalsClicked = useCallback(() => {
		if (selectedItems.length > 0) {
			dispatch(mapUnknownAnimals(selectedItems));
		}
	}, [dispatch, selectedItems]);

	return (
		<PageContainer className="map-unknown-animals-overview">
			<FlexRow className="map-unknown-container">
				<SkioldTableGrid
					columns={columns}
					tableKey={'mapUnknownAnimals3'}
					data={stemAnimals ?? []}
					ignoreSetCount={true}
					ColumnExtensions={columnExtensions}
					groupedColumns={groupedColumns}
					useSelection={true}
					selectedChanged={setSelectedItems}
					selectionHeader={localized('completed')}
				/>
				<SkioldIconSpinner
					showSpinner={showSpinner}
					title={'Save'}
					icon={SaveGreyIcon}
					onPress={mapUnknownAnimalsClicked}
				/>
			</FlexRow>
			<SkioldModal padding="0" isOpen={entranceModalOpen} close={closeModal}>
				<SowEvents
					closeEditModal={closeModal}
					theme="dark"
					transponderNumber={
						unknownAnimalItem?.transponder ? Number(unknownAnimalItem?.transponder) : undefined
					}
					penId={unknownAnimalItem?.newPenId}
					sectionId={selectedSectionId}
					onSaveEntrance={onUnknownAnimalCreates}
					shouldCloseOnSave={true}
				/>
			</SkioldModal>
		</PageContainer>
	);
});
